import { Box, createTheme, ThemeProvider } from '@mui/material'
import { StyledEngineProvider } from '@mui/material/styles'

import { useEventDeleteMutation, useGetEvent, useGetEventConfig } from 'api/events/events.api'
import { useUser } from 'api/users/users.api'
import { getMRTUserLocalization } from 'localization/localization.helper'
import type { MRT_Row } from 'material-react-table'
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table'
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import theme from 'styles/theme/theme'
import { colors } from 'styles/variables'
import { Button } from 'ui/atoms'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import ConfirmDialog from 'ui/components/ConfirmDialog/ConfirmDialog'
import TooltipIconButton from 'ui/components/TooltipIconButton/TooltipIconButton'
import Datetime from 'utils/datetime/datetime'
import EventChangeRequestModal from '../EventChangeRequestModal/EventChangeRequestModal'
import EventPostModal from '../EventPostModal/EventPostModal'
import EventPostViewModal from '../EventPostViewModal/EventPostViewModal'
import { EventPost, EventPostListItem, EventStatus, getLabelForEventStatus } from '../events.helper'

type EventsTableProps = {
  events: EventPostListItem[]
}

enum ROW_CLICK_ACTION_ENUM {
  VIEW = 'view',
  EDIT = 'edit',
}

export default function EventsTable({ events }: EventsTableProps): ReactElement {
  const { t } = useTranslation()
  const { systemId, hasAccess } = useAuth()
  const { data: user } = useUser()
  const userLocalization = getMRTUserLocalization(user?.settings.language)

  const eventConfig = useGetEventConfig(systemId).data
  const eventCategories = eventConfig?.categories

  const { mutateAsync: deleteEventPost } = useEventDeleteMutation()

  const language = localStorage.getItem(`language`) || 'sv'

  const [columns, setColumns] = useState<MRT_ColumnDef<EventPostListItem>[]>([])
  const [selectedEventPost, setSelectedEventPost] = useState<EventPostListItem | undefined>(undefined)
  const [selectedViewEventPost, setSelectedViewEventPost] = useState<EventPostListItem | undefined>(undefined)
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false)
  const [eventToDelete, setEventToDelete] = useState<EventPostListItem | undefined>(undefined)
  const [openChangeRequestModal, setOpenChangeRequestModal] = useState(false)
  const [changeRequestEventId, setChangeRequestEventId] = useState<number | undefined>(undefined)
  const [changeRequestEventPost, setChangeRequestEventPost] = useState<EventPost | undefined>(undefined)

  const CHANGE_REQUEST_PERMISSION = hasAccess({
    module: 'events',
    submodule: 'change_requests',
    permission: 'view_change_requests',
  })

  const fetchEventData = useGetEvent(changeRequestEventId)

  const hasEventsWithChangeRequest = useMemo(() => {
    return events.some((event) => event.pending_change_request_id)
  }, [events])

  const deleteEvent = useCallback(
    async (id: number) => {
      try {
        await deleteEventPost(id)
        alertStore.success(t(`Event deleted`))
      } catch (_e) {
        alertStore.error(t(`Failed to delete event`))
      }
    },
    [deleteEventPost, t]
  )

  const statusPriority = new Map([
    ['Sent', 0],
    ['Draft', 1],
    ['Active', 2],
    ['Inactive', 3],
  ])

  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      const priorityA = statusPriority.get(a.status)
      const priorityB = statusPriority.get(b.status)

      if (priorityA === priorityB) {
        const updatedAtA = Datetime.toISOString(a.updated_at ?? '')
        const updatedAtB = Datetime.toISOString(b.updated_at ?? '')
        return Datetime.compare(updatedAtB, updatedAtA)
      }

      return (priorityA ?? 0) - (priorityB ?? 0)
    })
  }, [events, statusPriority])

  const handleRowClick = useCallback((action: ROW_CLICK_ACTION_ENUM, row: { original: EventPostListItem }): void => {
    if (action === ROW_CLICK_ACTION_ENUM.VIEW) {
      setSelectedViewEventPost(row.original)
    } else if (action === ROW_CLICK_ACTION_ENUM.EDIT) {
      setSelectedEventPost(row.original)
    }
  }, [])

  const handleChangeRequestClick = useCallback((row: { original: EventPostListItem }): void => {
    setChangeRequestEventId(row.original.id)
    setOpenChangeRequestModal(true)
  }, [])

  useEffect(() => {
    if (fetchEventData.data && openChangeRequestModal) {
      setChangeRequestEventPost(fetchEventData.data)
    }
  }, [fetchEventData.data, openChangeRequestModal])

  const hasAccessToUMMCheckbox = hasAccess({ submodule: 'operational_event_umm', module: 'events' })
  useEffect(() => {
    const newColumns: MRT_ColumnDef<EventPostListItem>[] = [
      {
        accessorKey: 'name',
        header: t('Event'),
        Cell: ({ cell }) => {
          return <Box sx={{ display: 'inline' }}>{cell.getValue<string>()}</Box>
        },
        sortingFn: 'alphanumeric',
      },
      {
        accessorKey: 'start_time',
        header: t('Start time'),
        Cell: ({ row }: { row: MRT_Row<EventPostListItem> }) => {
          const start_time = Datetime.toLocalTime(Datetime.toISOString(row?.original?.start_time), 'longDayText')
          return <Box sx={{ display: 'inline' }}>{start_time}</Box>
        },
        size: 150,
        sortingFn: (rowA, rowB) => {
          const startTimeA = Datetime.toISOString(rowA?.original?.start_time)
          const startTimeB = Datetime.toISOString(rowB?.original?.start_time)
          return startTimeA.localeCompare(startTimeB)
        },
      },
      {
        accessorKey: 'end_time',
        header: t('End time'),
        Cell: ({ row }: { row: MRT_Row<EventPostListItem> }) => {
          const end_time = Datetime.toLocalTime(Datetime.toISOString(row?.original?.end_time), 'longDayText')
          return <Box sx={{ display: 'inline' }}>{end_time}</Box>
        },
        size: 150,
        sortingFn: (rowA, rowB) => {
          const endTimeA = Datetime.toISOString(rowA?.original?.end_time)
          const endTimeB = Datetime.toISOString(rowB?.original?.end_time)
          return endTimeA.localeCompare(endTimeB)
        },
      },
      {
        accessorKey: 'category_id',
        header: t('Cause category'),
        Cell: ({ row }: { row: MRT_Row<EventPostListItem> }) => {
          const categoryDisplayName = eventCategories?.find(
            (category) => category.id === row.original.category_id
          )?.name
          return <Box sx={{ display: 'inline' }}>{t(categoryDisplayName as string)}</Box>
        },
        size: 150,
        sortingFn: (rowA, rowB) => {
          const categoryA = eventCategories?.find((category) => category.id === rowA.original.category_id)
            ?.name as string
          const categoryB = eventCategories?.find((category) => category.id === rowB.original.category_id)
            ?.name as string
          return categoryA.localeCompare(categoryB, language)
        },
      },
      {
        accessorKey: 'status',
        header: t('Status'),
        Cell: ({ cell }) => {
          const cellValue = cell.getValue<EventStatus>()
          const displayName = t(getLabelForEventStatus(cellValue))
          let color
          if (cellValue === 'Active') {
            color = colors.success4
          } else if (cellValue === 'Inactive') {
            color = colors.grey6
          }
          return <Box sx={{ display: 'inline', color: color }}>{displayName}</Box>
        },
        filterFn: (row, columnId, filterValue) => {
          const cellValue = row.getValue<EventStatus>(columnId)
          const displayName = t(getLabelForEventStatus(cellValue))
          return displayName.toLowerCase().includes(filterValue.toLowerCase())
        },
        size: 10,
      },
      ...(CHANGE_REQUEST_PERMISSION && hasEventsWithChangeRequest
        ? [
            {
              accessorKey: 'change_request',
              header: t('Change request'),
              Cell: ({ row }: { row: MRT_Row<EventPostListItem> }) => {
                if (!row?.original?.pending_change_request_id) {
                  return '-'
                }

                return (
                  <TooltipIconButton
                    icon="fas fa-exclamation-circle"
                    iconSize="medium"
                    iconColor={colors.warning4}
                    tooltip={t('View change request')}
                    onClick={() => handleChangeRequestClick(row)}
                  />
                )
              },
              size: 10,
              enableSorting: true,
              enableColumnFilter: false,
            },
          ]
        : []),
      {
        accessorKey: 'updated_at',
        header: t('Created/Changed'),
        Cell: ({ row }) => {
          const updated_at = Datetime.toLocalTime(Datetime.toISOString(row?.original?.updated_at ?? ''), 'longDayText')
          return <Box sx={{ display: 'inline' }}>{updated_at}</Box>
        },
        size: 150,
        sortingFn: (rowA, rowB) => {
          const updatedAtA = Datetime.toISOString(rowA?.original?.updated_at ?? '')
          const updatedAtB = Datetime.toISOString(rowB?.original?.updated_at ?? '')
          return updatedAtA.localeCompare(updatedAtB)
        },
      },
      {
        accessorKey: 'manage',
        header: t('Manage'),
        Cell: ({ cell }) => (
          <Box sx={{ display: 'flex', gap: '2px' }}>
            <Button onClick={() => handleRowClick(ROW_CLICK_ACTION_ENUM.VIEW, cell.row)} primary>
              {t('Show')}
            </Button>
            <Button onClick={() => handleRowClick(ROW_CLICK_ACTION_ENUM.EDIT, cell.row)} primary>
              {t('Edit')}
            </Button>
          </Box>
        ),
        size: 10,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: 'delete',
        header: t('Delete'),
        Cell: ({ row }) => {
          const disableDelete =
            !row?.original?.permitted_actions?.includes('delete_event') || row?.original?.write === false
          return (
            <Box>
              <TooltipIconButton
                icon={'fas fa-trash'}
                iconColor={disableDelete ? colors.grey5 : colors.danger3}
                iconSize="small"
                tooltip={disableDelete ? t('You do not have permission to delete this event') : t('Delete event')}
                disabled={disableDelete}
                onClick={() => {
                  setEventToDelete(row?.original)
                  setConfirmDeleteModalOpen(true)
                }}
              />
            </Box>
          )
        },

        size: 10,
        enableSorting: false,
        enableColumnFilter: false,
      },
    ]

    if (hasAccessToUMMCheckbox) {
      newColumns.splice(5, 0, {
        accessorKey: 'require_umm',
        header: t('Requires UMM'),
        Cell: ({ cell }) => {
          return <Box sx={{ display: 'inline' }}>{cell.getValue() ? t('Yes') : '-'}</Box>
        },
        size: 10,
        sortingFn: (rowA, rowB) => {
          //Sorts by require_umm, true first
          const sameValue = rowA?.original?.require_umm == rowB?.original?.require_umm
          return sameValue ? 0 : rowA?.original?.require_umm ? -1 : 1
        },
      })
    }

    setColumns(newColumns)
  }, [
    eventCategories,
    t,
    handleRowClick,
    handleChangeRequestClick,
    hasAccessToUMMCheckbox,
    language,
    CHANGE_REQUEST_PERMISSION,
    hasEventsWithChangeRequest,
  ])

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: theme.palette.primary,
          secondary: theme.palette.secondary,
          info: theme.palette.info,
        },
      }),

    []
  )

  const customGlobalFilterFn = useCallback(
    (row: any, columnId: string, filterValue: string) => {
      const cellValue = row.getValue(columnId)
      const searchValue = filterValue.toLowerCase()

      // Handle special columns that need translation
      switch (columnId) {
        case 'status': {
          if (!cellValue) {
            return false
          }
          const translatedStatus = t(getLabelForEventStatus(cellValue as EventStatus))
          return translatedStatus.toLowerCase().includes(searchValue)
        }
        case 'category_id': {
          const categoryName = eventCategories?.find((category) => category.id === row.original.category_id)?.name

          if (categoryName) {
            const translatedCategory = t(categoryName)
            return translatedCategory.toLowerCase().includes(searchValue)
          } else {
            return row.original.category_id?.toString().includes(searchValue) ?? false
          }
        }
        default: {
          // Use the same logic as "includesString" for all other columns
          const stringValue = cellValue?.toString().toLowerCase() || ''
          return stringValue.includes(searchValue)
        }
      }
    },
    [t, eventCategories]
  )

  const columnOrder = [
    'name',
    'start_time',
    'end_time',
    'category_id',
    'status',
    'require_umm',
    'updated_at',
    'manage',
    'delete',
  ]

  return (
    <>
      <div style={{ width: '100%', overflowX: `scroll` }}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={tableTheme}>
            <MaterialReactTable
              columns={columns}
              data={sortedEvents}
              enableGlobalFilterModes
              enableColumnActions={false}
              enableColumnFilters={false}
              enableStickyHeader
              enableColumnDragging={false}
              paginateExpandedRows={false}
              positionGlobalFilter="left"
              localization={userLocalization}
              filterFns={{
                customGlobal: customGlobalFilterFn,
              }}
              globalFilterFn="customGlobal"
              enableGlobalFilterRankedResults={true}
              initialState={{
                columnOrder: columns.length > 0 ? columnOrder : undefined,
                showGlobalFilter: true,
              }}
              muiTableHeadCellProps={{
                sx: {
                  padding: '10px',
                },
              }}
              muiTableBodyCellProps={{
                sx: {
                  padding: '15px 10px',
                },
              }}
              muiTableBodyRowProps={({ row }) => ({
                sx: {
                  backgroundColor:
                    row.original.status === 'Sent'
                      ? colors.info2
                      : row.original.status === 'Draft'
                        ? colors.draft
                        : 'inherit',
                },
              })}
              muiSearchTextFieldProps={{
                placeholder: t(`Search`),
                variant: 'outlined',
              }}
              enablePagination={false}
              muiTableContainerProps={{
                sx: {
                  maxHeight: 'none',
                  overflowY: 'visible',
                  overflowX: 'auto',
                  width: '100%',
                },
              }}
              muiTablePaperProps={{
                sx: {
                  boxShadow: 'none',
                },
              }}
            />
          </ThemeProvider>
        </StyledEngineProvider>
      </div>

      {selectedEventPost && (
        <EventPostModal
          closeModal={() => setSelectedEventPost(undefined)}
          selectedEventPostId={selectedEventPost?.id}
        />
      )}

      {selectedViewEventPost && (
        <EventPostViewModal
          closeModal={() => setSelectedViewEventPost(undefined)}
          selectedEventPostId={selectedViewEventPost?.id}
        />
      )}

      {openChangeRequestModal && changeRequestEventPost && (
        <EventChangeRequestModal
          eventPost={changeRequestEventPost}
          mode={'read-only'}
          closeModal={() => {
            setChangeRequestEventPost(undefined)
            setOpenChangeRequestModal(false)
            setChangeRequestEventId(undefined)
          }}
        />
      )}

      <ConfirmDialog
        open={confirmDeleteModalOpen}
        onConfirm={() => {
          deleteEvent(eventToDelete?.id as number)
          setConfirmDeleteModalOpen(false)
          setEventToDelete(undefined)
        }}
        onClose={() => {
          setConfirmDeleteModalOpen(false)
          setEventToDelete(undefined)
        }}
        textObject={{
          title: t(`Are you sure you want to remove this event?`),
          text: t(`This event and it's settings will be deleted and you will not be able to recover it.`),
        }}
      />
    </>
  )
}
